import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Medal, ArrowRight, Sparkle, Clock, Confetti, Users, Star,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import client from "../lib/api";

/*
  ReferralLeaderboardPage — public leaderboard of top FreightPilot AI referrers.
  Route: /referrals/leaderboard

  Design goals:
    - Bold, competitive. Podium for top 3 (gold / silver / bronze).
    - Anonymized (first name + last initial only) — never exposes emails.
    - Public — no login needed. Great for trucker Facebook groups + LinkedIn.
    - Strong CTA to signup so visitors don't just browse — they join.
    - Fires zero conversion events (pure top-of-funnel awareness page).
*/

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"]; // Gold, silver, bronze

export default function ReferralLeaderboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Top FreightPilot AI Referrers — Leaderboard";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "See who's earning the most free trial days by referring other carriers to FreightPilot AI. Every referral = +30 days for both sides. Join the leaderboard."
      );
    }
    (async () => {
      try {
        const { data } = await client.get("/referrals/leaderboard/public?limit=25");
        setData(data);
      } catch {
        setData({ ranked: [], stats: { total_conversions: 0, total_referrers: 0, days_awarded_top_25: 0 } });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="referral-leaderboard-page">
      <Header />
      <Hero />
      {loading ? (
        <div className="max-w-4xl mx-auto py-16 text-center text-neutral-500 text-sm">Loading the leaderboard…</div>
      ) : (
        <>
          <GlobalStats stats={data.stats} />
          {data.ranked.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <Podium top3={data.ranked.slice(0, 3)} />
              {data.ranked.length > 3 && <FullList rows={data.ranked.slice(3)} />}
            </>
          )}
        </>
      )}
      <BottomCTA />
      <Footer />
    </div>
  );
}

/* ─── HEADER ─── */
function Header() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="lb-home"><Logo variant="sm" /></Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/customers" className="text-neutral-400 hover:text-white hidden sm:inline">Customers</Link>
          <Link to="/pricing" className="text-neutral-400 hover:text-white hidden sm:inline">Pricing</Link>
          <Link
            to="/signup"
            data-testid="lb-header-cta"
            className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-1.5"
          >
            Start free trial <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ─── HERO ─── */
function Hero() {
  return (
    <section className="pt-12 sm:pt-16 pb-8 border-b border-white/10" data-testid="lb-hero">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6">
          <Trophy size={12} weight="fill" /> LEADERBOARD · UPDATED HOURLY
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-5">
          Top carriers on the <span className="text-[#FF5A1F]">FreightPilot AI</span> referral leaderboard.
        </h1>
        <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          Every signup through your link = <b className="text-white">+30 days free trial</b> for both of you.
          These carriers are pulling in the most.
        </p>
      </div>
    </section>
  );
}

/* ─── GLOBAL STATS BAR ─── */
function GlobalStats({ stats }) {
  const items = [
    { icon: Users, label: "Referrers on the board", value: stats.total_referrers },
    { icon: Confetti, label: "Total signups referred", value: stats.total_conversions },
    { icon: Clock, label: "Days awarded (top 25)", value: `+${stats.days_awarded_top_25}` },
  ];
  return (
    <section className="py-10 border-b border-white/10 bg-white/[0.015]" data-testid="lb-global-stats">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 grid grid-cols-3 gap-3 sm:gap-6">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="text-center">
              <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                <Icon size={11} weight="fill" /> {it.label}
              </div>
              <div className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight">
                {it.value || 0}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─── PODIUM (top 3) ─── */
function Podium({ top3 }) {
  // Show 2nd, 1st, 3rd for the classic podium visual
  const positions = [1, 0, 2].map((idx) => top3[idx]).filter(Boolean);
  const heights = ["h-32 sm:h-40", "h-40 sm:h-56", "h-24 sm:h-32"];
  return (
    <section className="py-14 sm:py-20 border-b border-white/10" data-testid="lb-podium">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <div className="overline mb-3">🏆 TOP 3 REFERRERS</div>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-6 items-end">
          {positions.map((r, i) => {
            const trueRank = r.rank;
            const color = MEDAL_COLORS[trueRank - 1];
            const heightCls = heights[i];
            return (
              <div key={r.rank} className="flex flex-col items-center" data-testid={`podium-${trueRank}`}>
                <div className="text-center mb-3">
                  <Medal
                    size={trueRank === 1 ? 40 : 32}
                    weight="fill"
                    style={{ color }}
                    className="mx-auto mb-2"
                  />
                  <div className="font-display font-semibold text-white truncate max-w-[130px] sm:max-w-none text-sm sm:text-base">
                    {r.display_name}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {r.conversions} signup{r.conversions === 1 ? "" : "s"}
                  </div>
                  <div className="text-xs text-emerald-400 font-semibold mt-1">
                    +{r.days_earned}d earned
                  </div>
                </div>
                <div
                  className={`w-full ${heightCls} rounded-t-xl relative flex items-start justify-center pt-3`}
                  style={{
                    background: `linear-gradient(180deg, ${color}22 0%, ${color}0A 100%)`,
                    border: `1px solid ${color}55`,
                    borderBottom: "none",
                  }}
                >
                  <div
                    className="font-display font-bold"
                    style={{ color, fontSize: trueRank === 1 ? "48px" : "36px", lineHeight: 1 }}
                  >
                    {trueRank}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── FULL LIST (positions 4-25) ─── */
function FullList({ rows }) {
  return (
    <section className="py-14 sm:py-20 border-b border-white/10" data-testid="lb-full-list">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-8">
          <div className="overline mb-3">POSITIONS 4 – {3 + rows.length}</div>
        </div>
        <div className="card-surface divide-y divide-white/5">
          {rows.map((r) => (
            <div
              key={r.rank}
              className="p-4 sm:p-5 flex items-center gap-4"
              data-testid={`lb-row-${r.rank}`}
            >
              <div className="w-8 text-center text-neutral-500 font-mono text-sm shrink-0">
                #{r.rank}
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#B54313] flex items-center justify-center text-black font-semibold text-sm shrink-0">
                {(r.display_name || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-base font-medium text-white truncate">{r.display_name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {r.conversions} signup{r.conversions === 1 ? "" : "s"} · Since {r.since}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-base sm:text-lg font-display font-semibold text-emerald-400">
                  +{r.days_earned}d
                </div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-widest">earned</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── EMPTY STATE (before first conversion) ─── */
function EmptyState() {
  return (
    <section className="py-20 sm:py-28 border-b border-white/10">
      <div className="max-w-2xl mx-auto px-4 sm:px-8 text-center card-surface p-10">
        <Trophy size={44} weight="fill" className="text-[#FF5A1F]/50 mx-auto mb-4" />
        <h2 className="font-display text-2xl font-semibold mb-3">Leaderboard is still warming up.</h2>
        <p className="text-neutral-400 text-sm leading-relaxed mb-6">
          No referrals have converted yet. Sign up, grab your unique link,
          and be the FIRST name on this leaderboard.
        </p>
        <Link
          to="/signup"
          className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
          data-testid="lb-empty-cta"
        >
          Start free trial <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </section>
  );
}

/* ─── BOTTOM CTA ─── */
function BottomCTA() {
  return (
    <section className="py-20 sm:py-28 border-b border-white/10 relative overflow-hidden" data-testid="lb-bottom-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center relative z-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6">
          <Sparkle size={12} weight="fill" /> WANT ON THE BOARD?
        </div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5">
          Sign up, grab your link, invite one carrier.
        </h2>
        <p className="text-neutral-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">
          The moment you refer your first signup, your name lands on this
          board — and you both get <b className="text-white">+30 days</b> added
          to your trial. No cap.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/signup"
            data-testid="lb-bottom-primary-cta"
            className="btn-primary rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Start free trial <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/customers"
            className="rounded-full px-7 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <Star size={13} weight="fill" /> Read customer stories
          </Link>
        </div>
      </div>
      <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[500px] h-56 bg-[#FF5A1F]/10 rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  return (
    <footer className="py-8 sm:py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
        <div className="flex items-center gap-2">
          <Logo variant="sm" /> © {new Date().getFullYear()} FreightPilot AI
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </div>
    </footer>
  );
}
